/**
 * Silent device licensing for the native app (no-login model).
 *
 * The paid App Store app never shows an account. On first launch we mint a
 * stable device UUID, exchange it (plus the shared app key) at
 * POST /v2/auth/device for a Sanctum bearer token, and store the token in
 * the normal token store. From then on the app is simply "signed in" as its
 * shadow device user — ProtectedRoute, drafts, rankings, and the AI wallet
 * all work with zero UI.
 *
 * Storage: the device id must survive as long as possible (it IS the
 * license identity for drafts/cheatsheet continuity), so it prefers the
 * Capacitor Preferences bridge over WKWebView localStorage.
 *
 * The app key ships in the bundle (VITE_DEVICE_KEY) — an abuse speed bump,
 * not a secret. See laravel/config/device.php for the server-side notes.
 */
import { api } from '@/lib/apiClient';
import { API_ORIGIN } from '@/lib/apiClient';
import { nativePlatform } from '@/lib/platform';

const DEVICE_ID_KEY = 'de_device_id';

async function storageGet(key: string): Promise<string | null> {
  const prefs = window.Capacitor?.Plugins?.Preferences;
  if (prefs) {
    try {
      return (await prefs.get({ key })).value ?? null;
    } catch {
      /* fall through */
    }
  }
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

async function storageRemove(key: string): Promise<void> {
  try {
    const prefs = window.Capacitor?.Plugins?.Preferences;
    if (prefs) {
      await prefs.remove({ key });
    }
  } catch {
    /* fall through to localStorage */
  }
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* storage unavailable - nothing to remove */
  }
}

async function storageSet(key: string, value: string): Promise<void> {
  const prefs = window.Capacitor?.Plugins?.Preferences;
  if (prefs) {
    try {
      await prefs.set({ key, value });
      return;
    } catch {
      /* fall through */
    }
  }
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* unpersisted — a fresh id next launch just re-provisions */
  }
}

function newUuid(): string {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // WebView without randomUUID — RFC4122 v4 via getRandomValues.
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b: number) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

async function getOrCreateDeviceId(): Promise<string> {
  const existing = await storageGet(DEVICE_ID_KEY);
  if (existing) return existing;
  const id = newUuid();
  await storageSet(DEVICE_ID_KEY, id);
  return id;
}

/**
 * Ensure this device holds a valid bearer token, provisioning one if needed.
 * Resolves true when a token is in the store afterwards. Never throws.
 */
export async function ensureDeviceLicense(): Promise<boolean> {
  try {
    if (await api.tokenStore.get()) return true;

    const appKey = (import.meta.env.VITE_DEVICE_KEY as string | undefined) ?? '';
    if (!appKey) {
      // Build not configured for device licensing — fail quietly; the app
      // will behave as an anonymous free-tier client.
      return false;
    }

    // Hard timeout: this runs during BootstrapGate, so a hanging first
    // connection (server asleep, iOS granting local-network access late)
    // would hold the WHOLE app on the boot spinner — observed 2 minutes on
    // a real device. Fail fast instead; provisioning retries next launch
    // and the app boots as free-tier in the meantime.
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 8000);
    const resp = await fetch(`${API_ORIGIN}/v2/auth/device`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Device-Key': appKey,
      },
      body: JSON.stringify({
        device_id: await getOrCreateDeviceId(),
        platform: nativePlatform(),
      }),
      signal: abort.signal,
    }).finally(() => clearTimeout(timer));
    if (!resp.ok) return false;

    const data = (await resp.json()) as { token?: string };
    if (!data.token) return false;

    await api.tokenStore.set(data.token);
    return true;
  } catch {
    return false;
  }
}

/**
 * App Store 5.1.1(v) "Reset app data": erases the silent device account
 * server-side (drafts, cheatsheet, wallet, the account row itself), then
 * clears the local device id and bearer token so the next launch
 * provisions a brand-new blank identity.
 *
 * Returns true when the server confirmed the purge. Local identity is
 * cleared ONLY on server success - failing to purge remotely but wiping
 * locally would orphan the server data forever, the opposite of deletion.
 */
export async function purgeDeviceData(): Promise<boolean> {
  try {
    const token = await api.tokenStore.get();
    if (!token) return false;

    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 10000);
    const resp = await fetch(`${API_ORIGIN}/v2/auth/device/purge`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      signal: abort.signal,
    }).finally(() => clearTimeout(timer));
    if (!resp.ok) return false;

    await api.tokenStore.clear();
    await storageRemove(DEVICE_ID_KEY);
    await clearLocalAppData();
    return true;
  } catch {
    return false;
  }
}

/**
 * Wipe every scrap of app-owned state on the device.
 *
 * The server purge deletes the account; this deletes what never left the
 * phone. Skipping it left the Sleeper USERNAME, the linked league ids, and
 * the draft-room mappings sitting in localStorage, so "delete my data"
 * rebooted into a fresh anonymous account that still greeted the user by
 * their Sleeper handle. That is a third-party identifier surviving a
 * deletion the UI promised — an App Store privacy problem, not a cosmetic
 * one.
 *
 * Swept by PREFIX rather than an enumerated list: every key this app writes
 * is namespaced `de.` / `de_`, and a hand-maintained list silently stops
 * covering whatever key gets added next. Third-party keys are left alone.
 *
 * Never throws — a storage failure here must not turn a completed server
 * purge into a reported failure, which would invite the user to retry a
 * delete that already happened.
 */
async function clearLocalAppData(): Promise<void> {
  const ours = (k: string): boolean => k.startsWith('de.') || k.startsWith('de_');

  try {
    const prefs = window.Capacitor?.Plugins?.Preferences;
    if (prefs?.keys) {
      const { keys } = await prefs.keys();
      for (const k of keys ?? []) {
        if (ours(k)) await prefs.remove({ key: k });
      }
    }
  } catch {
    /* Preferences unavailable — the localStorage sweep below still runs. */
  }

  try {
    const doomed: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i);
      if (k && ours(k)) doomed.push(k);
    }
    // Collected first: removing during the walk reindexes the store and
    // would skip every other key.
    for (const k of doomed) window.localStorage.removeItem(k);
  } catch {
    /* storage unavailable — nothing persisted to remove */
  }
}
